import { message } from 'antd';
import { useCallback, useRef, useState } from 'react';

import { useTranslation } from '@/i18n/client';
import { executeFunction } from '@/service/realtime';
import { getOpenAIStreamASR } from '@/service/voice';
import useUserSettings from '@/store/useUserSettings';

export type RealtimeEventType =
  | 'session.update'
  | 'input_audio_buffer.append'
  | 'input_audio_buffer.commit'
  | 'input_audio_buffer.clear'
  | 'conversation.item.create'
  | 'conversation.item.truncate'
  | 'conversation.item.delete'
  | 'response.create'
  | 'response.cancel'
  | 'user.speech.start'
  | 'user.speech.end'
  | 'transcription_session.update';

export interface RealtimeTranscriptionEvent {
  type: RealtimeEventType;
  [key: string]: any;
}

export interface ResponseCreateEvent {
  type: 'response.create';
  response: {
    modalities: ('text' | 'image' | 'audio')[];
    instructions: string;
  };
}

export interface UseOpenAIRealtimeTranscriptionOptions {
  onEvent?: (event: RealtimeTranscriptionEvent) => void;
  onAudioTrack?: (audioElement: HTMLAudioElement) => void;
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  onError?: (error: Error) => void;
}

export interface UseOpenAIRealtimeTranscriptionReturn {
  connect: (options?: ConnectOptions) => Promise<void>;
  disconnect: () => void;
  sendEvent: (event: RealtimeTranscriptionEvent) => boolean;
  isConnected: boolean;
  isConnecting: boolean;
  connectionState: RTCPeerConnectionState | null;
  error: Error | null;
  recordingEnableToggle: (enabled: boolean) => void;
}

interface ConnectOptions {
  sessionId: string;
}

/**
 * 用于管理OpenAI Realtime WebRTC会话的Hook (V2版本，基于官方demo)
 */
export function useOpenAIRealtimeTranscription({
  onEvent,
  onAudioTrack,
  onError,
  onConnectionStateChange
}: UseOpenAIRealtimeTranscriptionOptions = {}): UseOpenAIRealtimeTranscriptionReturn {
  // 状态
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const isCloseRef = useRef<boolean>(false);

  const preferredLanguage = useUserSettings((state) => state.preferredLanguage);

  // 引用
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioTrackRef = useRef<MediaStreamTrack | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  const { t } = useTranslation();

  // 清理资源
  const cleanup = useCallback(() => {
    isCloseRef.current = true;

    // 关闭数据通道
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    // 停止所有媒体轨道
    if (peerConnectionRef.current) {
      peerConnectionRef.current.getSenders().forEach((sender) => {
        if (sender.track) {
          sender.track.stop();
        }
      });

      // 关闭连接
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // 重置状态
    setIsConnected(false);
    setIsConnecting(false);
    setConnectionState(null);
  }, []);

  const disconnect = useCallback(() => {
    cleanup();
  }, [cleanup]);

  // 发送事件到OpenAI
  const sendEvent = useCallback((event: RealtimeTranscriptionEvent) => {
    if (!dataChannelRef.current || dataChannelRef.current.readyState !== 'open') {
      return false;
    }

    try {
      dataChannelRef.current.send(JSON.stringify(event));

      return true;
    } catch (err) {
      return false;
    }
  }, []);

  // 处理function call
  const onFunctionCall = useCallback(
    (event: any) => {
      // 获取工具调用信息
      const _response = event.response;

      if (_response?.output && _response.output?.[0]?.type === 'function_call') {
        const toolCall = _response.output[0];

        try {
          // 解析参数
          const args = JSON.parse(toolCall.arguments || '{}');

          // 执行function call
          executeFunction({
            functionName: toolCall.name,
            args: args,
            functionCallId: toolCall.call_id
          }).then((res) => {
            if (res.data.code !== 0 || !res.data.data.success) {
              throw new Error(res.data.data.output || res.data.message);
            }

            const output = res.data.data.output;

            // 发送工具调用结果
            const toolResultEvent = {
              event_id: `client_${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
              type: 'conversation.item.create' as RealtimeEventType,
              item: {
                type: 'function_call_output',
                call_id: toolCall.call_id,
                output: output
              }
            };

            sendEvent(toolResultEvent);
            // Force a model response to make sure it responds after tool call
            sendEvent({
              type: 'response.create'
            });
          });
        } catch (_error: any) {
          // 发送错误结果
          const errorResultEvent = {
            event_id: `client_${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            type: 'conversation.item.create' as RealtimeEventType,
            item: {
              type: 'function_call_output',
              call_id: toolCall.call_id,
              output: `Failed to execute function: ${_error.message || 'Unknown error'}`
            }
          };

          sendEvent(errorResultEvent);
          // Force a model response to make sure it responds after tool call
          sendEvent({
            type: 'response.create'
          });
        }
      }
    },
    [sendEvent]
  );

  // 连接到OpenAI Realtime服务
  const connect = useCallback(async () => {
    if (isConnecting || isConnected) {
      return;
    }

    isCloseRef.current = false;

    setIsConnecting(true);
    setError(null);

    try {
      // 1. 获取会话信息
      const sessionResponse = await getOpenAIStreamASR(preferredLanguage);

      if (sessionResponse.data.code !== 0) {
        throw new Error(`Realtime Session Error: ${sessionResponse.data.message}`);
      }

      const data = sessionResponse.data.data;

      if (!data.token) {
        throw new Error(t('error.oops'));
      }

      if (isCloseRef.current) {
        throw new Error(t('error.oops'));
      }

      // 2. 创建WebRTC连接 - 简化为官方demo的实现
      const pc = new RTCPeerConnection();

      peerConnectionRef.current = pc;

      // 3. 设置音频元素
      const audioEl = document.createElement('audio');

      audioEl.autoplay = true;
      audioElementRef.current = audioEl;

      // 4. 处理远程音频轨道
      pc.ontrack = (e) => {
        audioEl.srcObject = e.streams[0];

        if (onAudioTrack) onAudioTrack(audioEl);
      };

      if (isCloseRef.current) {
        throw new Error(t('error.oops'));
      }

      // 5. 添加本地音频轨道
      try {
        const ms = await navigator.mediaDevices.getUserMedia({
          audio: true
        });

        mediaStreamRef.current = ms;
        audioTrackRef.current = ms.getTracks()[0];

        pc.addTrack(ms.getTracks()[0]);
      } catch (mediaError) {
        message.error('Cannot access microphone');
        // 继续执行，即使没有麦克风权限
      }

      // 监听连接状态变化
      pc.addEventListener('connectionstatechange', () => {
        const state = pc.connectionState;

        setConnectionState(state);

        if (state === 'connected') {
          setIsConnected(true);
          setIsConnecting(false);
        } else if (state === 'disconnected' || state === 'failed' || state === 'closed') {
          setIsConnected(false);
          setIsConnecting(false);

          if (state === 'failed') {
            cleanup();
          }
        }

        if (onConnectionStateChange) onConnectionStateChange(state);
      });

      if (isCloseRef.current) {
        throw new Error(t('error.oops'));
      }

      // 6. 创建本地数据通道
      const dc = pc.createDataChannel('events');

      // 监听本地数据通道事件
      dc.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);

        sendEvent({
          type: 'transcription_session.update',
          session: {
            input_audio_format: 'pcm16',
            // turn_detection: null,
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: 800
            },
            // modalities: ['audio'],
            // input_audio_noise_reduction: null,
            input_audio_noise_reduction: {
              type: 'near_field' // 'far_field' | 'near_field'
            },
            input_audio_transcription: {
              model: data.customParam.model,
              language: preferredLanguage
            }
          }
        });
      };

      dc.onclose = () => {
        setIsConnected(false);
      };

      dc.onerror = (err) => {
        throw new Error(err.error.errorDetail);
      };

      // 监听本地数据通道消息
      dc.onmessage = (e) => {
        try {
          const realtimeEvent = JSON.parse(e.data);

          if (realtimeEvent.type === 'response.done') {
            // onFunctionCall(realtimeEvent);
            console.log('111', realtimeEvent);
          }

          if (onEvent) {
            onEvent(realtimeEvent);
          }
        } catch (parseError) {
          console.error(
            'WebRTC DataChannel Error: Failed to parse local event data:',
            parseError,
            e.data
          );
        }
      };

      dataChannelRef.current = dc;

      if (isCloseRef.current) {
        throw new Error(t('error.oops'));
      }

      // 7. 创建和发送SDP offer
      const offer = await pc.createOffer({
        offerToReceiveAudio: true
      });

      await pc.setLocalDescription(offer);

      const baseUrl = 'https://api.openai.com/v1/realtime';
      const sdpResponse = await fetch(`${baseUrl}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${data.token}`,
          'Content-Type': 'application/sdp'
        }
      });

      if (!sdpResponse.ok) {
        console.error('SDP Response Error:', sdpResponse.status, sdpResponse.statusText);
        const errorText = await sdpResponse.text().catch(() => '');

        throw new Error(
          `WebRTC Connection Error: ${sdpResponse.status} ${sdpResponse.statusText} ${errorText}`
        );
      }

      // 8. 设置远程描述
      const sdpText = await sdpResponse.text();

      const answer = {
        type: 'answer',
        sdp: sdpText
      };

      await pc.setRemoteDescription(answer as RTCSessionDescriptionInit);

      if (isCloseRef.current) {
        throw new Error(t('error.oops'));
      }
    } catch (err) {
      console.error('WebRTC Connection Error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));

      if (onError) onError(err instanceof Error ? err : new Error(String(err)));

      cleanup();
    }
  }, [
    isConnecting,
    isConnected,
    onAudioTrack,
    onConnectionStateChange,
    cleanup,
    sendEvent,
    onEvent,
    onFunctionCall,
    onError
  ]);

  const recordingEnableToggle = useCallback((enabled: boolean) => {
    if (audioTrackRef.current) {
      audioTrackRef.current.enabled = enabled;
    }
  }, []);

  return {
    connect,
    disconnect,
    sendEvent,
    isConnected,
    isConnecting,
    connectionState,
    error,
    recordingEnableToggle
  };
}
